import type { ComponentCategory } from '@smartphone-tycoon/shared-core';
import type { ResearchUnlock } from '@smartphone-tycoon/shared-core';

export class ResearchIntegrationService {
  /**
   * Filter available components based on research unlocks
   */
  static filterComponentsByResearch(
    components: ComponentCategory[],
    researchUnlocks: ResearchUnlock[]
  ): ComponentCategory[] {
    // Get all component unlock IDs
    const unlockedComponentIds = researchUnlocks
      .filter(unlock => unlock.type === 'component')
      .map(unlock => unlock.id);

    // If no research unlocks, return base tier components only
    if (unlockedComponentIds.length === 0) {
      return components.map(category => ({
        ...category,
        options: category.options.filter(option => 
          // Only show tier 1 (base) components
          !option.value.includes('tier2') && 
          !option.value.includes('tier3') &&
          !option.value.includes('quantum') &&
          !option.value.includes('neural') &&
          !option.value.includes('holographic') &&
          !option.value.includes('5g_plus')
        )
      }));
    }

    // Filter components based on unlocks
    return components.map(category => {
      let filteredOptions = [...category.options];

      // Apply research-based filtering
      switch (category.id) {
        case 'cpu':
          if (!unlockedComponentIds.includes('cpu_tier2')) {
            filteredOptions = filteredOptions.filter(opt => 
              !opt.value.includes('tier2') && !opt.value.includes('quantum')
            );
          }
          if (!unlockedComponentIds.includes('cpu_tier3')) {
            filteredOptions = filteredOptions.filter(opt => !opt.value.includes('quantum'));
          }
          break;

        case 'gpu':
          if (!unlockedComponentIds.includes('gpu_tier2')) {
            filteredOptions = filteredOptions.filter(opt => !opt.value.includes('tier2'));
          }
          break;

        case 'battery':
          if (!unlockedComponentIds.includes('battery_tier2')) {
            filteredOptions = filteredOptions.filter(opt => !opt.value.includes('tier2'));
          }
          break;

        case 'camera':
          if (!unlockedComponentIds.includes('camera_tier2')) {
            filteredOptions = filteredOptions.filter(opt => 
              !opt.value.includes('advanced') && !opt.value.includes('multi')
            );
          }
          break;

        case 'network':
          if (!unlockedComponentIds.includes('network_5g_plus')) {
            filteredOptions = filteredOptions.filter(opt => !opt.value.includes('5g_plus'));
          }
          break;
      }

      return {
        ...category,
        options: filteredOptions
      };
    });
  }

  /**
   * Get available design options based on research unlocks
   */
  static getAvailableDesignOptions(researchUnlocks: ResearchUnlock[]) {
    const designUnlocks = researchUnlocks
      .filter(unlock => unlock.type === 'design_option')
      .map(unlock => unlock.id);

    return {
      materials: {
        // Base materials always available
        plastic: true,
        aluminum: true,
        // Research-gated materials
        matte: designUnlocks.includes('material_matte'),
        glossy: designUnlocks.includes('material_glossy'),
        metallic: designUnlocks.includes('material_metallic'),
        glass: designUnlocks.includes('material_glass'),
        titanium: designUnlocks.includes('material_titanium'),
        ceramic: designUnlocks.includes('material_ceramic'),
        wood: designUnlocks.includes('material_wood'),
        carbonFiber: designUnlocks.includes('material_carbon_fiber'),
        sapphire: designUnlocks.includes('material_sapphire'),
        transparent: designUnlocks.includes('material_transparent')
      },
      formFactors: {
        // Base form factor always available
        standard: true,
        // Research-gated form factors
        foldable: designUnlocks.includes('form_foldable'),
        flip: designUnlocks.includes('form_flip'),
        modular: designUnlocks.includes('form_modular')
      },
      sizes: {
        // Base sizes always available
        small: true,
        medium: true,
        large: true,
        // Research-gated sizes
        compact: designUnlocks.includes('size_compact'),
        tablet: designUnlocks.includes('size_tablet')
      },
      features: {
        curvedEdges: designUnlocks.includes('design_curved'),
        aiPhotography: designUnlocks.includes('ai_photography'),
        parallelResearch: designUnlocks.includes('parallel_research')
      }
    };
  }

  /**
   * Apply stat improvements to game mechanics
   */
  static applyStatImprovements(
    baseValue: number,
    improvementType: string,
    statImprovements: Record<string, number>
  ): number {
    const improvement = statImprovements[improvementType] || 0;
    
    // For cost reductions, apply as negative percentage
    if (improvementType.includes('cost') || improvementType === 'defect_rate') {
      return baseValue * (1 - improvement / 100);
    }
    
    // For improvements, apply as positive percentage
    return baseValue * (1 + improvement / 100);
  }

  /**
   * Calculate modified production cost with research bonuses
   */
  static calculateModifiedProductionCost(
    baseCost: number,
    statImprovements: Record<string, number>
  ): number {
    let modifiedCost = baseCost;
    
    // Apply production cost reduction
    if (statImprovements.production_cost) {
      modifiedCost *= (1 - statImprovements.production_cost / 100);
    }
    
    // Apply component cost reduction
    if (statImprovements.component_cost) {
      modifiedCost *= (1 - statImprovements.component_cost / 100);
    }
    
    // Apply overall cost reduction
    if (statImprovements.all_costs) {
      modifiedCost *= (1 - statImprovements.all_costs / 100);
    }
    
    return Math.round(modifiedCost);
  }

  /**
   * Calculate modified monthly costs with research bonuses
   */
  static calculateModifiedMonthlyCosts(
    baseCosts: number,
    statImprovements: Record<string, number>
  ): number {
    let modifiedCosts = baseCosts;
    
    // Apply monthly cost reduction
    if (statImprovements.monthly_costs) {
      modifiedCosts *= (1 - statImprovements.monthly_costs / 100);
    }
    
    // Apply overall cost reduction
    if (statImprovements.all_costs) {
      modifiedCosts *= (1 - statImprovements.all_costs / 100);
    }
    
    return Math.round(modifiedCosts);
  }
}